/**
 * LINKURIOUS CONFIDENTIAL
 * Copyright Linkurious SAS 2012 - 2018
 *
 * Created by maximeallex on 2018-04-11.
 */


'use strict';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef, Component, ComponentFactoryResolver, HostListener, NgZone, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import {filter, map, pairwise} from 'rxjs/operators';

import {
  CloseMenuEvent,
  ContextMenuEvent,
  LKContextMenuService, LoadingMenuEvent
} from './service';
import { ContextElementDirective } from './element';
import {Tools} from "linkurious-shared";
import {Subscription} from "rxjs/Subscription";

const BASE_MARGIN = 20;

@Component({
  selector: 'lk-context-menu',
  template: `<div #curtain
     *ngIf="curtain"
     class="back_curtain"
     [class.active]="!closed && !isLoading"></div>
<div #wrapper
     class="context_menu"
     [class.none]="closed"
     [ngStyle]="position">
  <div class="close_wrapper" *ngIf="cross && !closed">
    <icon name='close' stroke size="15" (click)="close($event)"></icon>
  </div>
  <ng-template context-element></ng-template>
  <label class="progress_spinner" *ngIf="isLoading">
    <span class="spinner"></span>
  </label>
</div>
<div #arrow
     *ngIf="arrow"
     class="arrow"
     [ngStyle]="arrowPosition">
</div>

`,
  styles: [`.context_menu{background:#fff;border:1px solid #cbcbcb;box-shadow:0 6px 12px rgba(0,0,0,.175);-o-box-shadow:0 6px 12px rgba(0,0,0,.175);-ms-box-shadow:0 6px 12px rgba(0,0,0,.175);-moz-box-shadow:0 6px 12px rgba(0,0,0,.175);-webkit-box-shadow:0 6px 12px rgba(0,0,0,.175);left:-9999px;max-height:250px;position:fixed;top:-9999px;display:block;width:250px;z-index:9998;overflow-y:inherit}.arrow{border-bottom:12px solid transparent;border-left:0 solid transparent;border-right:11px solid #cbcbcb;border-top:12px solid transparent;height:0;position:fixed;width:0;z-index:9999}.arrow:before{border-bottom:10px solid transparent;border-left:0 solid transparent;border-right:10px solid #fff;border-top:10px solid transparent;content:'';height:0;left:1px;position:absolute;top:-10px;width:0}.back_curtain{position:fixed;width:100%;height:100%;left:0;top:0;opacity:0;visibility:hidden;background:0 0;transition:opacity linear .2s;z-index:9997}.back_curtain.opaque{background:rgba(255,255,255,.5)}.back_curtain.active{opacity:1;visibility:visible}.close_wrapper{display:flex;justify-content:flex-end}.close_wrapper icon{cursor:pointer;padding:10px 10px 5px}.context_menu__close{position:absolute;right:20px;top:11px;cursor:pointer}.progress_spinner{position:absolute;top:7px;right:7px;z-index:2;width:100%;height:100%;display:flex;justify-content:space-around;align-items:center}.progress_spinner .spinner{display:block;border:2px solid #d9d9d9;border-top:2px solid #878787;border-radius:50%;width:19px;height:19px;-webkit-animation:2s linear infinite spin;animation:2s linear infinite spin;margin:0!important}@-webkit-keyframes spin{0%{-webkit-transform:rotate(0);transform:rotate(0)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}@keyframes spin{0%{-webkit-transform:rotate(0);transform:rotate(0)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}`],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LKContextMenuComponent implements OnInit, OnDestroy {

  @ViewChild('wrapper') private _wrapper;
  @ViewChild('curtain') private _curtain;
  @ViewChild(ContextElementDirective) contextElement: ContextElementDirective;
  @ViewChild('arrow')   private _arrow;
  public componentRef: any;
  public position: {
    top: string;
    left: string;
    width: string;
    'max-height'?: string;
  } = {
    top: '-9999px',
    left: '-9999px',
    width: '0'
  };
  public arrowPosition: {
    left: string;
    top: string;
    transform?: string;
  } = {
    left: '-9999px',
    top: '-9999px'
  };
  public arrow: boolean;
  public cross: boolean;
  public curtain: boolean;
  public closed: boolean = true;
  public opened: boolean = false;
  public triggerElement: HTMLElement;
  public isLoading: boolean = false;
  private _cursorPosition: {x: number; y: number};
  private _disallowClose: boolean = false;
  private _openingSubscription: Subscription;
  private _closingSubscription: Subscription;
  private _loadingSubscription: Subscription;

  constructor(
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _detectorRef: ChangeDetectorRef,
    private _service: LKContextMenuService,
    private _ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this._openingSubscription = this._service.event$.pipe(filter(e => e instanceof ContextMenuEvent))
      .subscribe(e => this.openContextMenu(e as ContextMenuEvent));
    this._closingSubscription = this._service.event$.pipe(filter(e => e instanceof CloseMenuEvent))
      .subscribe(() => this.close());
    this._loadingSubscription = this._service.event$.pipe(filter(e => e instanceof LoadingMenuEvent))
      .subscribe((e) => this.load(e as LoadingMenuEvent));

    this._ngZone.runOutsideAngular(() => {
      document.addEventListener('scroll', this.listenScroll.bind(this));
      document.addEventListener('click', this.clickOutside.bind(this));
      document.addEventListener('keyup', this.escape.bind(this));
    });
  }

  ngOnDestroy() {
    this._openingSubscription.unsubscribe();
    this._closingSubscription.unsubscribe();
    document.removeEventListener('scroll', this.listenScroll.bind(this));
    document.removeEventListener('click', this.clickOutside.bind(this));
    document.removeEventListener('keyup', this.escape.bind(this));
  }

  private escape(e: KeyboardEvent): void {
    if ( e.key === 'Escape' ) {
      this._service.close();
    }
  }

  private clickOutside(e: MouseEvent): void {
    if ( !this.closed && !this.curtain ) {
      if ( this._disallowClose && (this._wrapper && !this._wrapper.nativeElement.contains(e.target))) {
        this.close();
      } else if ( !this._disallowClose ) {
        this.close();
      }
    }
    if ( this.triggerElement ) {
      if ( this._disallowClose ) {
        if ( !this.closed && ( this._curtain && this._curtain.nativeElement === e.target ) ) {
          this._service.close();
        }
      } else if ( !this.closed ) {
        this._service.close();
      }
    } else if ( this._cursorPosition ) {
      if ( !this.closed ) {
        this._service.close();
      }
    }
  }

  /**
   * Listen to scroll events on page and close the contextmenu if a scroll event happens outside of
   * the component
   */
  private listenScroll(e): void {
    if ( this.componentRef && !this._wrapper.nativeElement.contains(e.target) ) {
      this._service.close();
    }
  }

  /**
   * Return the absolute position of the trigger element in page
   */
  private get _triggerBoundingRect(): {
    width: number;
    height: number;
    top: number;
    bottom: number;
    left: number;
    right: number
  } {
    return ( this.triggerElement )
      ? this.triggerElement.getBoundingClientRect()
      : {
        width: 0,
        height: 0,
        top: this._cursorPosition.y,
        bottom: this._cursorPosition.y,
        left : this._cursorPosition.x,
        right: this._cursorPosition.x
      }
  }

  /**
   * Return the HTML element of the context menu
   */
  private get _cMenuEl(): HTMLElement {
    return this._wrapper.nativeElement;
  }


  /**
   * Close the menu
   */
  public close(e?: MouseEvent): void {
    this.isLoading = false;
    if ( Tools.isDefined(e) ) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.closed = true;
    if ( Tools.isDefined(this.componentRef) ) {
      this.componentRef.destroy();
    }
    if ( this.triggerElement ) {
      this.triggerElement.classList.remove('context_menu--opened');
    }
    this.curtain = false;
    this.arrowPosition.top = '-9999px';
    this.arrowPosition.left = '-9999px';
    this.arrowPosition.transform = 'none';
    this.position.top = '-9999px';
    this.position.left = '-9999px';
    this.opened = false;
    this._detectorRef.markForCheck();
  }

  private load(e: LoadingMenuEvent): void {
    this.close();
    this.triggerElement = e.trigger;
    this._cursorPosition = e.cursorPosition;
    setTimeout(() => {
      if ( !this.opened ) {
        if ( !e.position ) {
          this.position['height'] = LKContextMenuComponent.toPixels(e.height);
          this.setPosition(e.width, e.height);
        } else {
          this.forcePosition(e.width, e.height, e.position, true);
        }
        this.closed = false;
        this.isLoading = true;
        this._detectorRef.markForCheck();
      }
    }, 0);
  }

  /**
   * Position the context menu and show it
   */
  private openContextMenu(e: ContextMenuEvent): void {
    this.opened = true;
    this.curtain = e.curtain;
    this.cross = e.cross;
    let componentFactory = this._componentFactoryResolver.resolveComponentFactory(e.menu);
    let viewContainerRef = this.contextElement.viewContainerRef;
    viewContainerRef.clear();
    this.componentRef = viewContainerRef.createComponent(componentFactory);
    if (e.props) {
      Object.keys(e.props).forEach(componentProperty => {
        this.componentRef.instance[componentProperty] = e.props[componentProperty];
      });
    }
    this.triggerElement = e.trigger;
    this._disallowClose = ( e.disallowClose ) ? e.disallowClose : false;
    this._cursorPosition = e.cursorPosition;
    if ( this.triggerElement ) {
      this.triggerElement.classList.add('context_menu--opened');
    }
    this.arrow = (e.position === 'relative') ? false : e.arrow;
    setTimeout(() => {
      if ( !e.position ) {
        this.setPosition(e.width, e.height);
      } else {
        this.forcePosition(e.width, e.height, e.position);
      }
      this.isLoading = false;
      this.closed = false;
      this._detectorRef.markForCheck();
    }, 0);
  }

  /**
   * Call the right positioning method depending of the value set
   */
  private forcePosition(width: number, height: number, position: 'top'|'left'|'right'|'bottom'|'relative', isLoading?: boolean): void {
    this.position.width = (width) ? LKContextMenuComponent.toPixels(width) : '250px';
    this.position['max-height'] = (height) ? LKContextMenuComponent.toPixels(height) : '250px';
    this.position['height'] = (isLoading) ? LKContextMenuComponent.toPixels(height) : undefined;
    setTimeout(() => {
      switch(position) {
        case 'top' :
          this.positionTop();
          break;

        case 'right' :
          this.positionRight();
          break;

        case 'bottom' :
          this.positionBottom();
          break;

        case 'relative' :
          this.positionRelative();
          break;

        default:
          this.positionLeft();
      }
    }, 0);
  }

  /**
   * Position the context-menu relatively to its trigger element
   */
  private positionRelative(): void {
    let baseLeft = this._triggerBoundingRect.left;
    let baseTop = this._triggerBoundingRect.bottom + 5;

    if ( baseLeft + this._cMenuEl.offsetWidth > document.documentElement.clientWidth ) {
      baseLeft = this._triggerBoundingRect.right - this._cMenuEl.offsetWidth;
    }

    if ( baseTop + this._cMenuEl.offsetHeight > document.documentElement.clientHeight ) {
      baseTop = this._triggerBoundingRect.top - this._cMenuEl.offsetHeight - 5;
    }
    this.position.left = LKContextMenuComponent.toPixels(baseLeft);
    this.position.top = LKContextMenuComponent.toPixels(baseTop);
    this._detectorRef.markForCheck();
  }

  /**
   * Position the context-menu to top
   */
  private positionTop(): void {
    this.position.top = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.top - this._cMenuEl.offsetHeight - BASE_MARGIN
    );
    this.position.left = LKContextMenuComponent.toPixels(
      (this._triggerBoundingRect.left + (this._triggerBoundingRect.width / 2)) - ( this._cMenuEl.offsetWidth / 2 )
    );
    this.arrowPosition.top = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.top - BASE_MARGIN - 8
    );
    this.arrowPosition.left = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.left + (this._triggerBoundingRect.width / 2)
    );
    this.arrowPosition.transform = 'rotate(-90deg)';
    this._detectorRef.markForCheck();
  }

  /**
   * Position the context-menu to bottom
   */
  private positionBottom(): void {
    this.position.top = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.bottom + BASE_MARGIN
    );
    this.position.left = LKContextMenuComponent.toPixels(
      (this._triggerBoundingRect.left + (this._triggerBoundingRect.width / 2)) - ( this._cMenuEl.offsetWidth / 2 )
    );
    this.arrowPosition.top = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.bottom + 4
    );
    this.arrowPosition.left = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.left + (this._triggerBoundingRect.width / 2)
    );
    this.arrowPosition.transform = 'rotate(90deg)';
    this._detectorRef.markForCheck();
  }

  /**
   * Position the context-menu to left
   */
  private positionLeft(): void {
    this.position.top = LKContextMenuComponent.toPixels(
      (this._triggerBoundingRect.top + (this._triggerBoundingRect.height / 2)) - (this._cMenuEl.offsetHeight / 2)
    );
    this.position.left = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.left - this._cMenuEl.offsetWidth - BASE_MARGIN
    );
    this.arrowPosition.top = LKContextMenuComponent.toPixels(
      (this._triggerBoundingRect.top + this._triggerBoundingRect.height / 2) - 12
    );
    this.arrowPosition.left = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.left - BASE_MARGIN - 1
    );
    this.arrowPosition.transform = 'rotate(180deg)';
    this._detectorRef.markForCheck();
  }

  /**
   * Position the context-menu to right
   */
  private positionRight(): void {
    this.position.top = LKContextMenuComponent.toPixels(
      (this._triggerBoundingRect.top + (this._triggerBoundingRect.height / 2)) - (this._cMenuEl.offsetHeight / 2)
    );
    this.position.left = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.right + BASE_MARGIN
    );
    this.arrowPosition.top = LKContextMenuComponent.toPixels(
      (this._triggerBoundingRect.top + this._triggerBoundingRect.height / 2) - 12
    );
    this.arrowPosition.left = LKContextMenuComponent.toPixels(
      this._triggerBoundingRect.right + 10
    );
    this._detectorRef.markForCheck();
  }


  /**
   * Set the position of the menu
   */
  private setPosition(width: number, height: number): void {
    let viewPortWidth = document.documentElement.clientWidth;
    let viewPortHeight = document.documentElement.clientHeight;
    let triggerPosition = this._triggerBoundingRect;
    let contextMenuEl = this._wrapper.nativeElement;
    let menuWidth = (width) ? width : 250;
    let maxHeight = (height) ? height : 250;
    let baseLeft = triggerPosition.right + BASE_MARGIN;
    let baseTop;
    let menuHeight;
    let finalLeft;
    let finalTop;

    // Set size of the context menu
    this.position.width = (width) ? LKContextMenuComponent.toPixels(width) : '250px';
    this.position['max-height'] = (height) ? LKContextMenuComponent.toPixels(height) : '250px';

    setTimeout(() => {
      // set the final size of the context menu
      menuHeight = (contextMenuEl.offsetHeight < maxHeight)
        ? contextMenuEl.offsetHeight
        : maxHeight;
      // set the default top position of the context menu
      baseTop = triggerPosition.top - ( (menuHeight - triggerPosition.height) / 2 );

      if ( baseTop <= 0 ) {
        // If context menu overflow the top edge of the screen, position the top at the top of the element
        finalTop = BASE_MARGIN;
      } else if ( menuHeight + baseTop >= viewPortHeight ) {
        // If context menu overflow the bottom edge of the screen, position the bottom at the bottom of the element
        finalTop = viewPortHeight - menuHeight - BASE_MARGIN;
      } else {
        finalTop = baseTop;
      }
      // set position of the arrow
      let arrowBaseTop = triggerPosition.top + (triggerPosition.height / 2);
      let arrowBaseBottom = triggerPosition.top + (triggerPosition.height / 2) + 12;
      if ( arrowBaseTop  < finalTop ) {
        this.arrowPosition.top = LKContextMenuComponent.toPixels(finalTop);
      } else if ( arrowBaseBottom > finalTop + menuHeight ) {
        this.arrowPosition.top = LKContextMenuComponent.toPixels(finalTop + menuHeight - 24);
      } else {
        this.arrowPosition.top = LKContextMenuComponent.toPixels(triggerPosition.top + (triggerPosition.height / 2) - 12);
      }
      this.position.top = LKContextMenuComponent.toPixels(finalTop);

      if ( baseLeft + menuWidth >= viewPortWidth ) {
        // If context menu overflow the right edge of the screen, position menu on the left of the element
        finalLeft = triggerPosition.left - menuWidth - BASE_MARGIN;
        this.arrowPosition.left = LKContextMenuComponent.toPixels(triggerPosition.left - 19);
        this.arrowPosition.transform = 'rotate(180deg)';
      } else {
        finalLeft = baseLeft;
        this.arrowPosition.left = LKContextMenuComponent.toPixels(triggerPosition.left + triggerPosition.width + 10);
      }
      this.position.left = LKContextMenuComponent.toPixels(finalLeft);

      this._detectorRef.markForCheck();
    }, 0);
  }

  /**
   * Set a number to string of this format : '${num]px'
   */
  private  static toPixels(num: number): string {
    return num + 'px';
  }
}
